import React, { useEffect, useState, useRef } from 'react'
import { useRouter } from '@src/routes'
import DefaultLayout, {
  LayoutSpacingOptions,
} from '@src/components/DefaultLayout'
import {
  makeStyles,
  Theme,
  Typography,
  Box,
  Container,
  Button,
} from '@material-ui/core/'
import LiquidInput from '@src/components/LiquidInput'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '@src/routes/ScanNow/messages'
import Link from '@material-ui/core/Link'
import ScanNowDetail from './ScanNowDetails'
import ScanNowFAQModal from './ScanNowFAQModal'
import ScanNowUsageModal from './ScanNowUsageModal'

const useStyles = makeStyles((theme: Theme) => ({
  subTitle: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  scanNowForm: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  scanInput: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '620px',
    },
  },
  scanResultsSection: {
    borderTop: `1px solid ${theme.palette.grey[400]}`,
  },
  scanLinks: {
    display: 'flex',
    flexDirection: 'column',
  },
  modalLinks: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(4),
    '& a': {
      cursor: 'pointer',
    },
    [theme.breakpoints.up('md')]: {
      '& span:last-child': {
        marginTop: theme.spacing(6),
      },
      flexDirection: 'column',
      marginBottom: theme.spacing(6),
    },
  },
  triggerLink: {
    color: theme.palette.primary.main,
    fontWeight: 900,
    textDecoration: 'none',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  linkContent: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  linkIconContainer: {
    display: 'flex',
    height: theme.typography.pxToRem(20),
    width: theme.typography.pxToRem(20),
    fontSize: theme.typography.pxToRem(20),
    marginRight: theme.spacing(3),
  },
  resetButton: {
    width: '100%',
    height: 48,
    marginTop: theme.spacing(4),
    [theme.breakpoints.up('md')]: {
      width: 'auto',
      height: 45,
      marginTop: 0,
      marginLeft: theme.spacing(4),
    },
  },
}))

const ScanNow = () => {
  const classes = useStyles()
  const intl = useIntl()
  const router = useRouter()
  const searchInput = useRef<any>(null)
  const { barcode = '' } = router.query || {}
  const [barcodeInput, setBarcodeInput] = useState<string>('')
  const [decodedBarcode, setBarcode] = useState<string>('')
  const [usageModalOpen, setUsageModalOpen] = useState<boolean>(false)
  const [faqModalOpen, setFaqModalOpen] = useState<boolean>(false)
  const [init, setInit] = useState<boolean>(false)

  const parseBarcode = (barcode: string) => {
    try {
      const parsedBarcode = (
        barcode?.startsWith('fnc') ? barcode?.substring(3) : barcode
      )
        ?.replace(/DataMatrix/g, '%5Dd2')
        ?.replace(/fnc/g, '%1D')
      return decodeURI(parsedBarcode)
    } catch (e) {
      return ''
    }
  }

  useEffect(() => {
    if (!init) {
      setInit(true)
    }
  })

  useEffect(() => {
    const parsedBarcode = parseBarcode(barcode)
    setBarcode(parsedBarcode)
    setBarcodeInput(parsedBarcode)
  }, [router?.query])

  if (!init) return null
  return (
    <DefaultLayout spacing={LayoutSpacingOptions.SM}>
      <Container>
        <Box mb={6}>
          <Typography variant="h1" component="h1">
            <FormattedMessage id="SCAN_NOW" defaultMessage="Scan Now" />
          </Typography>
          <Typography variant="h2" component="h2" className={classes.subTitle}>
            <FormattedMessage {...messages.SCAN_NOW_NOTICE} />
          </Typography>

          <Box className={classes.modalLinks}>
            <span>
              <Link onClick={() => setUsageModalOpen(true)}>
                {intl.formatMessage(messages.HOW_DO_I_DO_THIS)}
              </Link>
            </span>
            <span>
              <Link onClick={() => setFaqModalOpen(true)}>
                {intl.formatMessage(messages.FAQ)}
              </Link>
            </span>
          </Box>
          <div className={classes.scanNowForm}>
            {}
            <LiquidInput
              value={barcodeInput}
              className={classes.scanInput}
              multiline
              onChange={(e) => setBarcodeInput(e.target.value.trim())}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  const parsedBarcode = parseBarcode(barcodeInput)
                  setBarcodeInput(parsedBarcode)
                  setBarcode(parsedBarcode)
                }
              }}
              inputRef={searchInput}
              autoFocus
              spellCheck={false}
            />
            <Button
              type="submit"
              color="primary"
              variant="contained"
              onClick={() => {
                const parsedBarcode = parseBarcode(barcodeInput)
                setBarcodeInput(parsedBarcode)
                setBarcode(parsedBarcode)
                searchInput?.current?.focus()
              }}
              className={classes.resetButton}
            >
              {intl.formatMessage(messages.SUBMIT)}
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="outlined"
              onClick={() => {
                setBarcodeInput('')
                setBarcode('')
                router.push('/scan-now')
                searchInput?.current?.focus()
              }}
              className={classes.resetButton}
            >
              {intl.formatMessage(messages.RESET)}
            </Button>
          </div>
        </Box>
      </Container>
      <ScanNowDetail barcode={decodedBarcode} />

      <ScanNowUsageModal
        open={usageModalOpen}
        setModalOpen={setUsageModalOpen}
      />
      <ScanNowFAQModal open={faqModalOpen} setModalOpen={setFaqModalOpen} />
    </DefaultLayout>
  )
}

export default ScanNow
